import { motion } from "framer-motion";
import { Element } from "react-scroll";
import { FaChartBar, FaArrowRight } from "react-icons/fa";
import { WhatsappLink } from "..";
import { FaArrowTrendUp, FaBullseye, FaChartLine } from "react-icons/fa6";

const AnalyticsDashboardSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Key benefits based on real analytics capabilities
  const benefits = [
    {
      icon: FaChartBar,
      title: "Real-Time Click Analytics",
      description: "Track total clicks per item and see exactly which menu items customers interact with most. Identify hidden gems and optimize your top performers.",
    },
    {
      icon: FaArrowTrendUp,
      title: "View Duration Insights",
      description: "Measure average time spent viewing each item in seconds. Understand which dishes spark curiosity and which need better presentation or descriptions.",
    },
    {
      icon: FaChartLine,
      title: "Monthly Trend Tracking",
      description: "Historical data showing clicks and time spent patterns over time. Spot seasonal favorites, identify declining items.",
    },
    {
      icon: FaBullseye,
      title: "Category Performance",
      description: "Aggregate analytics across menu categories. Discover which sections drive the most engagement and strategically position your offerings.",
    },
  ];

  return (
    <Element id="analytics" name="analytics" className="py-20 relative border-b-2 border-orange-100">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-slate-900 leading-tight">
            Stop Guessing<br />
            <span className="bg-gradient-to-r from-orange-700 to-orange-500 bg-clip-text text-transparent">Start Knowing</span>
          </h2>
          <p className="text-lg lg:text-xl text-orange-700 max-w-2xl mx-auto leading-relaxed">
            See exactly which menu items customers love, how long they spend deciding, and what drives orders all in real-time.
          </p>
        </div>

        {/* Value Proposition Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border-2 border-slate-200 p-8 mb-16 -lg"
        >
          <div className="flex items-start gap-4">
            <div>
              <h3 className="text-2xl font-bold text-center text-slate-900 mb-3">
                Every Click Tells a Story
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Most restaurants guess which dishes are popular. With Dine.ai, you <strong>know</strong>. Our analytics dashboard tracks every customer interaction from quick glances to long considerations giving you data-driven insights to optimize your menu, pricing, and marketing strategy.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-orange-600 mb-1">103</div>
                  <div className="text-sm text-slate-600">Clicks on Cappuccino</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-orange-600 mb-1">16.3m</div>
                  <div className="text-sm text-slate-600">Average View Time</div>
                </div>
                {/* <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-orange-600 mb-1">+23%</div>
                  <div className="text-sm text-slate-600">Monthly Growth</div>
                </div> */}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Images with enhanced descriptions */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col h-full"
          >
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                <FaChartBar className="text-orange-500" />
                Real-Time Dashboard
              </h3>
              <p className="text-slate-600 mb-4">
                See which items get clicked most, how long customers view them, and overall engagement metrics. Update your menu strategy based on real customer behavior, not assumptions.
              </p>
            </div>
            <div className="mt-auto w-full rounded-xl overflow-hidden border-2 border-orange-200 shadow-lg hover:shadow-xl transition-shadow duration-300 aspect-video">
              <img 
                src="/analytics-dashboard.png" 
                alt="Analytics Dashboard showing real-time clicks, view times, and item performance metrics" 
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col h-full"
          >
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                <FaChartLine className="text-orange-500" />
                Monthly Trends
              </h3>
              <p className="text-slate-600 mb-4">
                Track performance over time to identify seasonal favorites, spot declining items early, and predict customer preferences. Make data-driven decisions about menu updates and promotions.
              </p>
            </div>
            <div className="mt-auto w-full rounded-xl overflow-hidden border-2 border-orange-200 shadow-lg hover:shadow-xl transition-shadow duration-300 aspect-video">
              <img 
                src="/monthly-trends.png" 
                alt="Monthly trends chart showing clicks and time spent patterns over time" 
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>

        {/* Key Benefits Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              {/* Card */}
              <div className="h-full relative bg-white rounded-2xl border border-slate-200 p-8 overflow-hidden transition-all duration-300 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-500/10">
                {/* Hover gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full justify-center items-center text-center">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-3xl shadow-lg shadow-orange-500/30 group-hover:shadow-orange-500/50 transition-all duration-300"
                  >
                    <benefit.icon />
                  </motion.div>

                  {/* Title */}
                  <h4 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                    {benefit.title}
                  </h4>

                  {/* Description */}
                  <p className="text-sm text-slate-600 leading-relaxed flex-grow group-hover:text-slate-700 transition-colors duration-300">
                    {benefit.description}
                  </p>

                  {/* Bottom accent line */}
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.05 }}
                    className="mt-6 h-1 bg-gradient-to-r from-orange-500 to-transparent rounded-full"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <div className="text-center">
          <p className="text-orange-700 text-lg mb-6">
            Ready to see your menu's true performance?
          </p>
          <a
            href={WhatsappLink}
            target="_blank"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-lg shadow-orange-400/30 hover:shadow-orange-500/50"
          >
            Get In Touch
            <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </div>
    </Element>
  );
};

export default AnalyticsDashboardSection;
